import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { FileText, Plus, Pencil, Trash2, X, Download, Wrench, Search, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const EMPTY = { client_id: "", vehicle_id: "", lignes: [], remise: 0, tva_rate: 20, statut: "brouillon", notes: "" };

const STATUTS = {
  brouillon: { label: "Brouillon", color: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  valide: { label: "Validé", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  refuse: { label: "Refusé", color: "bg-red-500/15 text-red-400 border-red-500/30" },
  transforme: { label: "Transformé en fiche", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
};

export default function Devis() {
  const navigate = useNavigate();
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stock, setStock] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selStock, setSelStock] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [d, c, v, s] = await Promise.all([
      api.get("/devis"), api.get("/clients"), api.get("/vehicles"), api.get("/stock"),
    ]);
    setDevis(d.data); setClients(c.data); setVehicles(v.data); setStock(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => {
    setForm({ client_id: d.client_id || "", vehicle_id: d.vehicle_id || "", lignes: d.lignes || [], remise: d.remise, tva_rate: d.tva_rate, statut: d.statut, notes: d.notes || "" });
    setEditId(d.id); setOpen(true);
  };

  const addLine = () => setForm((f) => ({ ...f, lignes: [...f.lignes, { designation: "", quantite: 1, prix_unitaire: 0, mo_incluse: true }] }));
  const addFromStock = () => {
    const item = stock.find((s) => s.id === selStock);
    if (!item) return;
    setForm((f) => ({ ...f, lignes: [...f.lignes, { designation: item.designation, quantite: 1, prix_unitaire: item.prix_vente, mo_incluse: true }] }));
    setSelStock("");
  };
  const setLine = (i, k, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], [k]: k === "designation" ? v : Number(v) }; return { ...f, lignes: l }; });
  const setLineMO = (i, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], mo_incluse: v }; return { ...f, lignes: l }; });
  const rmLine = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, x) => x !== i) }));

  const ht = form.lignes.reduce((a, l) => a + l.quantite * l.prix_unitaire, 0);
  const htRemise = ht - Number(form.remise || 0);
  const tva = htRemise * (Number(form.tva_rate || 0) / 100);
  const ttc = htRemise + tva;

  const clientVehicles = vehicles.filter((v) => v.client_id === form.client_id);

  const save = async () => {
    const payload = {
      ...form, remise: Number(form.remise), tva_rate: Number(form.tva_rate),
      lignes: form.lignes.map((l) => ({ designation: l.designation, quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire), mo_incluse: l.mo_incluse !== false })),
    };
    try {
      if (editId) await api.put(`/devis/${editId}`, payload);
      else await api.post("/devis", payload);
      toast.success("Devis enregistré"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    await api.delete(`/devis/${id}`); toast.success("Devis supprimé"); load();
  };

  const sendEmail = async (d) => {
    if (!d.client?.email) { toast.error("Ce client n'a pas d'adresse email renseignée."); return; }
    if (!window.confirm(`Envoyer le devis ${d.numero} à ${d.client.email} ?`)) return;
    try {
      await api.post(`/devis/${d.id}/send-email`);
      toast.success(`Devis envoyé à ${d.client.email}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const transformer = async (d) => {
    if (!window.confirm(`Transformer le devis ${d.numero} en fiche de réparation ?`)) return;    try {
      await api.post(`/devis/${d.id}/to_repair`);
      toast.success("Fiche de réparation créée depuis le devis");
      load();
      navigate("/reparations");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async (d) => {
    try {
      const res = await api.get(`/devis/${d.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `devis-${d.numero}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) { toast.error("Erreur lors de la génération du PDF"); }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="devis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Devis</h1>
          <p className="text-[#94A3B8] text-sm">Établissez des devis et exportez-les en PDF. (Facturation gérée séparément.)</p>
        </div>
        <Button data-testid="create-devis-button" onClick={openNew} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Nouveau devis
        </Button>
      </div>

      <div className="grid gap-3">
        {devis.map((d) => (
          <div key={d.id} data-testid={`devis-card-${d.id}`} className="bg-[#182232] border border-[#2A364F] rounded-xl p-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center"><FileText className="w-6 h-6" /></div>
              <div>
                <div className="font-heading font-semibold text-[#F8FAFC] flex items-center gap-2 flex-wrap">{d.numero}
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${(STATUTS[d.statut] || STATUTS.brouillon).color}`}>{(STATUTS[d.statut] || STATUTS.brouillon).label}</span>
                </div>
                <div className="text-sm text-[#94A3B8]">{d.client?.nom || "Client —"} · {d.vehicle?.immatriculation || "Véhicule —"} · {d.created_at?.slice(0, 10)}</div>
                {(d.lignes || []).length > 0 && (
                  <div data-testid={`devis-lignes-preview-${d.id}`} className="mt-2 flex flex-wrap items-center gap-1.5">
                    {(d.lignes).slice(0, 4).map((l, i) => (
                      <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-[#0A0D14] border border-[#2A364F] text-[#CBD5E1]">{l.designation} <span className="text-[#64748B]">×{l.quantite}</span>{l.mo_incluse === false && <span className="ml-1 text-amber-400">MO+</span>}</span>
                    ))}
                    {d.lignes.length > 4 && <span className="text-[11px] text-[#64748B]">+{d.lignes.length - 4} autre{d.lignes.length - 4 > 1 ? "s" : ""}</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-mono font-bold text-[#F59E0B]">{Number(d.total_ttc).toFixed(2)} € TTC</div>
                <div className="text-xs text-[#64748B]">{Number(d.total_ht).toFixed(2)} € HT</div>
              </div>
              {d.statut === "valide" && (
                <button data-testid={`devis-to-repair-${d.id}`} onClick={() => transformer(d)} className="h-9 px-3 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500 hover:text-white flex items-center gap-1.5 text-sm" title="Transformer en fiche de réparation"><Wrench className="w-4 h-4" />Transformer</button>
              )}
              <button data-testid={`devis-send-email-${d.id}`} onClick={() => sendEmail(d)} className="w-9 h-9 rounded-lg hover:bg-[#0A0D14] text-[#94A3B8] hover:text-emerald-400 flex items-center justify-center" title="Envoyer par email"><Mail className="w-4 h-4" /></button>
              <button data-testid={`preview-devis-${d.id}`} onClick={() => setPreview(d)} className="w-9 h-9 rounded-lg hover:bg-[#0A0D14] text-[#94A3B8] hover:text-cyan-400 flex items-center justify-center" title="Aperçu"><Search className="w-4 h-4" /></button>
              <button data-testid={`devis-pdf-export-button-${d.id}`} onClick={() => downloadPdf(d)} className="w-9 h-9 rounded-lg bg-[#3B82F6]/15 text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white flex items-center justify-center" title="Télécharger PDF"><Download className="w-4 h-4" /></button>
              <button data-testid={`edit-devis-${d.id}`} onClick={() => openEdit(d)} className="w-9 h-9 rounded-lg hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-devis-${d.id}`} onClick={() => del(d.id)} className="w-9 h-9 rounded-lg hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {devis.length === 0 && <div className="text-center text-[#64748B] py-10">Aucun devis.</div>}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="devis-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Search className="w-5 h-5 text-cyan-400" />Aperçu du devis</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-semibold">{preview.numero}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded border ${(STATUTS[preview.statut] || STATUTS.brouillon).color}`}>{(STATUTS[preview.statut] || STATUTS.brouillon).label}</span>
              </div>
              <div className="text-[#94A3B8]">{preview.client?.nom || "Client —"} · {preview.vehicle?.immatriculation || "Véhicule —"} · {(preview.created_at || "").slice(0, 10)}</div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#64748B] mb-1">Lignes</div>
                {(preview.lignes || []).map((l, i) => (
                  <div key={i} className="flex justify-between border-b border-[#2A364F]/50 py-1">
                    <span className="text-[#CBD5E1]">{l.designation} <span className="text-[#64748B]">×{l.quantite}</span>{l.mo_incluse === false && <span className="ml-1 text-amber-400 text-[10px]">MO+</span>}</span>
                    <span className="font-mono text-[#94A3B8]">{(l.quantite * l.prix_unitaire).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              {preview.notes && <div className="text-[#94A3B8] text-xs">💬 {preview.notes}</div>}
              <div className="bg-[#0A0D14] border border-[#2A364F] rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-[#94A3B8]"><span>Total HT</span><span className="font-mono">{Number(preview.total_ht).toFixed(2)} €</span></div>
                {Number(preview.remise) > 0 && <div className="flex justify-between text-[#94A3B8]"><span>Remise</span><span className="font-mono">-{Number(preview.remise).toFixed(2)} €</span></div>}
                <div className="flex justify-between text-[#94A3B8]"><span>TVA ({preview.tva_rate}%)</span><span className="font-mono">{Number(preview.total_tva).toFixed(2)} €</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-[#2A364F]"><span>Total TTC</span><span className="font-mono text-[#F59E0B]">{Number(preview.total_ttc).toFixed(2)} €</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Fermer</Button>
            {preview && preview.statut === "valide" && <Button data-testid="preview-devis-transform" onClick={() => { const d = preview; setPreview(null); transformer(d); }} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D14] font-semibold"><Wrench className="w-4 h-4 mr-1" />Transformer</Button>}
            {preview && <Button onClick={() => { const d = preview; setPreview(null); openEdit(d); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Modifier</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier le devis" : "Nouveau devis"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8]">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, vehicle_id: "" })}>
                  <SelectTrigger data-testid="devis-client-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Véhicule</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger data-testid="devis-vehicle-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue placeholder="Choisir un véhicule" /></SelectTrigger>
                  <SelectContent>{clientVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8]">Statut du devis</Label>
              <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                <SelectTrigger data-testid="devis-statut-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="valide">Validé</SelectItem>
                  <SelectItem value="refuse">Refusé</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#64748B] mt-1">Un devis « Validé » peut être transformé en fiche de réparation depuis la liste.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#94A3B8]">Lignes du devis</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={addLine} className="h-7 border-[#2A364F] bg-transparent text-[#94A3B8]"><Plus className="w-3 h-3 mr-1" />Ligne libre</Button>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <Select value={selStock} onValueChange={setSelStock}>
                  <SelectTrigger data-testid="devis-stock-select" className="bg-[#182232] border-[#2A364F] flex-1"><SelectValue placeholder="Ajouter depuis le stock" /></SelectTrigger>
                  <SelectContent>{stock.map((s) => <SelectItem key={s.id} value={s.id}>{s.designation} · {Number(s.prix_vente).toFixed(2)} €</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" onClick={addFromStock} disabled={!selStock} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.lignes.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={l.designation} onChange={(e) => setLine(i, "designation", e.target.value)} placeholder="Désignation" className="bg-[#182232] border-[#2A364F] flex-1" />
                    <button type="button" data-testid={`devis-line-mo-${i}`} onClick={() => setLineMO(i, l.mo_incluse === false ? true : false)} title="Main d'œuvre incluse dans le prix de la ligne (décocher si facturée à côté)" className={`shrink-0 text-[10px] px-2 py-1 rounded border whitespace-nowrap ${l.mo_incluse === false ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>{l.mo_incluse === false ? "MO en sus" : "MO incluse"}</button>
                    <Input type="number" value={l.quantite} onChange={(e) => setLine(i, "quantite", e.target.value)} className="bg-[#182232] border-[#2A364F] w-16" title="Qté" />
                    <Input type="number" step="0.01" value={l.prix_unitaire} onChange={(e) => setLine(i, "prix_unitaire", e.target.value)} className="bg-[#182232] border-[#2A364F] w-24" title="P.U." />
                    <span className="text-sm font-mono text-[#94A3B8] w-24 text-right">{(l.quantite * l.prix_unitaire).toFixed(2)} €</span>
                    <button onClick={() => rmLine(i)} className="text-[#64748B] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {form.lignes.length === 0 && <p className="text-xs text-[#64748B]">Ajoutez des lignes depuis le stock ou en saisie libre.</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#94A3B8]">Remise (€ HT)</Label><Input type="number" step="0.01" value={form.remise} onChange={(e) => setForm({ ...form, remise: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
              <div><Label className="text-[#94A3B8]">TVA (%)</Label><Input type="number" step="0.1" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            </div>

            <div><Label className="text-[#94A3B8]">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" rows={2} /></div>

            <div className="bg-[#0A0D14] border border-[#2A364F] rounded-lg p-4 space-y-1 text-sm">
              <div className="flex justify-between text-[#94A3B8]"><span>Total HT</span><span className="font-mono">{ht.toFixed(2)} €</span></div>
              {Number(form.remise) > 0 && <div className="flex justify-between text-[#94A3B8]"><span>Remise</span><span className="font-mono">-{Number(form.remise).toFixed(2)} €</span></div>}
              <div className="flex justify-between text-[#94A3B8]"><span>TVA ({form.tva_rate}%)</span><span className="font-mono">{tva.toFixed(2)} €</span></div>
              <div className="flex justify-between text-[#F8FAFC] font-bold pt-1 border-t border-[#2A364F]"><span>Total TTC</span><span className="font-mono text-[#F59E0B]" data-testid="devis-ttc">{ttc.toFixed(2)} €</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-devis-button" onClick={save} disabled={form.lignes.length === 0} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
